import { useState, useEffect, useRef, useCallback } from "react";
import api from "../axiosConfig";
import packageJson from "../../package.json";

/**
 * Hook personalizado para verificar actualizaciones de versión
 * Verifica automáticamente cada 5 minutos si hay una nueva versión disponible
 */
const useVersionChecker = () => {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [serverVersion, setServerVersion] = useState(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Obtener versión local del package.json
  const getLocalVersion = () => {
    try {
      // La versión se obtiene desde las variables de entorno en tiempo de build
      return process.env.REACT_APP_VERSION || packageJson.version || "2.3.0";
    } catch (error) {
      console.error("Error obteniendo versión local:", error);
      return "2.3.0"; // Fallback
    }
  };

  // Verificar versión del servidor
  const checkServerVersion = useCallback(async () => {
    try {
      setIsChecking(true);
      setError(null);

      const response = await api.get("/version");

      if (response.data.success) {
        const localVersion = getLocalVersion();
        const remoteVersion = response.data.version;

        setCurrentVersion(localVersion);
        setServerVersion(remoteVersion);

        // Verificar si ya se actualizó recientemente
        const lastUpdatedVersion = localStorage.getItem(
          "mogotax_last_updated_version"
        );
        const lastUpdateTime = localStorage.getItem("mogotax_last_update_time");
        const currentTime = Date.now();
        const oneMinute = 60 * 1000;

        // Si se actualizó en el último minuto a la misma versión, no mostrar notificación
        const recentlyUpdated =
          lastUpdatedVersion === remoteVersion &&
          lastUpdateTime &&
          currentTime - parseInt(lastUpdateTime) < oneMinute;

        // Comparar versiones
        const isUpdateNeeded =
          localVersion !== remoteVersion && !recentlyUpdated;
        setIsUpdateAvailable(isUpdateNeeded);

        // Actualización disponible o ya actualizado
      } else {
        throw new Error(response.data.message || "Error al verificar versión");
      }
    } catch (error) {
      console.error("Error verificando versión del servidor:", error);
      setError(error.message || "Error de conexión");
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Iniciar verificación periódica
  const startVersionCheck = useCallback(() => {
    // Verificar inmediatamente
    checkServerVersion();

    // Configurar verificación cada 5 minutos (300,000 ms)
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      checkServerVersion();
    }, 300000); // 5 minutos
  }, [checkServerVersion]);

  // Detener verificación periódica
  const stopVersionCheck = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Forzar actualización manual
  const forceUpdate = () => {

    // Marcar que se está actualizando
    if (serverVersion) {
      localStorage.setItem("mogotax_last_updated_version", serverVersion);
      localStorage.setItem("mogotax_last_update_time", Date.now().toString());
    }

    // Limpiar todos los caches disponibles
    if ("caches" in window) {
      caches
        .keys()
        .then((cacheNames) => {
          const deletePromises = cacheNames.map((cacheName) => {
            return caches.delete(cacheName);
          });

          Promise.all(deletePromises).then(() => {
            performReload();
          });
        })
        .catch(() => {
          performReload();
        });
    } else {
      performReload();
    }
  };

  // Función para realizar la recarga
  const performReload = () => {
    // Agregar timestamp para invalidar cache completamente
    const timestamp = Date.now();

    // Opción 1: Recargar con parámetros únicos
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("_t", timestamp);
    currentUrl.searchParams.set("_v", "3.0.0.51");

    // Recargando con URL actualizada

    // Usar replace para evitar historial
    window.location.replace(currentUrl.href);
  };

  // Reiniciar verificación
  const recheckVersion = () => {
    checkServerVersion();
  };

  // Inicializar el hook
  useEffect(() => {
    startVersionCheck();

    // Cleanup al desmontar
    return () => {
      stopVersionCheck();
    };
  }, [startVersionCheck]);

  // Verificar cuando la ventana vuelve a tener foco
  useEffect(() => {
    const handleFocus = () => {
      checkServerVersion();
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkServerVersion]);

  return {
    currentVersion,
    serverVersion,
    isUpdateAvailable,
    isChecking,
    error,
    checkServerVersion,
    startVersionCheck,
    stopVersionCheck,
    forceUpdate,
    recheckVersion,
  };
};

export default useVersionChecker;
